// app/customs/binaryviewer/index.tsx
'use client';

import React, { useEffect, useMemo, useState } from "react";
import * as Global from "@utils/global";
import { BinaryType } from "@utils/global";
import MyModal from '@/customs/mymodal';
import VideoPlayer from '@/customs/videoplayer';
import "./index.css";

interface BinaryViewerProps {
  user_domain: string;
  bin_guid: string | null;
  storageType?: "file" | "db";
  binaryType?: BinaryType;
  loadDbBinaryBody?: () => Promise<string | null>;
}

interface BinaryMeta {
  ok: boolean;
  mime: string | null;
  original_name: string | null;
  has_thumbnail: boolean;
}

interface DbBinaryJson {
  original_name?: string;
  mime?: string;
  body_base64?: string;
  thumbnail_mime?: string;
  thumbnail_base64?: string;
  body_omitted?: boolean;
}

const decodeBase64Text = (base64Text: string | undefined): string => {
  if (!base64Text) return "";
  const binary = window.atob(base64Text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder("utf-8").decode(bytes);
};

const BinaryViewer: React.FC<BinaryViewerProps> = ({
  user_domain,
  bin_guid,
  storageType = "file",
  binaryType = BinaryType.LOW,
  loadDbBinaryBody,
}) => {
  const [loadedDbBinaryValue, setLoadedDbBinaryValue] = useState<string | null>(null);
  const [meta, setMeta] = useState<BinaryMeta | null>(null);
  // const [open, setOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [emlOpen, setEmlOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  // const [binOpen, setBinOpen] = useState(false);

  // サイズ定義
  const canvasSize =
    binaryType === BinaryType.LOW ? "64px" :
    binaryType === BinaryType.MID ? "120px" : "240px";

  const dbBinarySource = loadedDbBinaryValue || bin_guid;
  const dbBinary = useMemo<DbBinaryJson | null>(() => {
    if (storageType !== "db" || !dbBinarySource) return null;
    try {
      return JSON.parse(dbBinarySource) as DbBinaryJson;
    } catch (err) {
      console.error("dbbinary parse error:", err);
      return null;
    }
  }, [dbBinarySource, storageType]);

  const dbBinaryDataUrl = dbBinary?.body_base64
    ? `data:${dbBinary.mime ?? "application/octet-stream"};base64,${dbBinary.body_base64}`
    : "";
  const dbBinaryBodyAvailable = storageType !== "db" || Boolean(dbBinary?.body_base64);
  const dbBinaryThumbUrl = dbBinary?.thumbnail_base64
    ? `data:${dbBinary.thumbnail_mime ?? "image/png"};base64,${dbBinary.thumbnail_base64}`
    : `${Global.getApiBaseUrl()}/spread/get_binary_icon?mime=${encodeURIComponent(dbBinary?.mime ?? "application/octet-stream")}`;

  // 用途別URL設定
  const fileThumbUrl =
    `${Global.getApiBaseUrl()}/spread/get_binary_thumbnail` +
    `?user_domain=${user_domain}&bin_guid=${bin_guid}`;
  const fileMetaUrl =
    `${Global.getApiBaseUrl()}/spread/get_binary_meta` +
    `?user_domain=${user_domain}&bin_guid=${bin_guid}`;
  const fileBinaryViewUrl =
    `${Global.getApiBaseUrl()}/spread/get_binary_view` +
    `?user_domain=${user_domain}&bin_guid=${bin_guid}`;
  const fileBinaryDownloadUrl =
    `${Global.getApiBaseUrl()}/spread/get_binary_download` +
    `?user_domain=${user_domain}&bin_guid=${bin_guid}`;
  const fileEmlToHtmlUrl =
    `${Global.getApiBaseUrl()}/spread/get_eml_to_html` +
    `?user_domain=${user_domain}&bin_guid=${bin_guid}`;

  const effectiveMeta = storageType === "db"
    ? {
        ok: true,
        mime: dbBinary?.mime ?? null,
        original_name: dbBinary?.original_name ?? null,
        has_thumbnail: Boolean(dbBinary?.thumbnail_base64),
      }
    : meta;
  const thumbUrl = storageType === "db" ? dbBinaryThumbUrl : fileThumbUrl;
  const binaryViewUrl = storageType === "db" ? dbBinaryDataUrl : fileBinaryViewUrl;
  const binaryDownloadUrl = storageType === "db" ? dbBinaryDataUrl : fileBinaryDownloadUrl;
  const emlToHtmlUrl = storageType === "db" ? "" : fileEmlToHtmlUrl;

  useEffect(() => {
    if (!bin_guid) return;
    if (storageType === "db") return;
    fetch(fileMetaUrl)
      .then(r => r.json())
      .then(setMeta)
      .catch(err => console.error("meta fetch error:", err));
  }, [bin_guid, fileMetaUrl, storageType]);

  if (!bin_guid) return null;

  const isImage = effectiveMeta?.mime?.startsWith("image/");
  const isVideo = effectiveMeta?.mime?.startsWith("video/");
  const isPdf = effectiveMeta?.mime === "application/pdf";
  const isEml = effectiveMeta?.mime === "message/rfc822" && storageType !== "db";
  const isText =
    effectiveMeta?.mime?.startsWith("text/") ||
    effectiveMeta?.mime === "application/json" ||
    effectiveMeta?.mime === "application/xml" ||
    effectiveMeta?.mime === "text/csv";
  const dbBinaryText = storageType === "db" ? decodeBase64Text(dbBinary?.body_base64) : "";

  const openPreview = (mime: string | null | undefined) => {
    if (mime?.startsWith("image/")) setImageOpen(true);
    else if (mime?.startsWith("video/")) setVideoOpen(true);
    else if (mime === "application/pdf") setPdfOpen(true);
    else if (mime === "message/rfc822" && storageType !== "db") setEmlOpen(true);
    else if (mime?.startsWith("text/") || mime === "application/json" || mime === "application/xml") setTextOpen(true);
    else if (binaryViewUrl) window.open(binaryViewUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <div className="binary-thumb-container">
        <div className="thumb-wrapper" style={{ width: canvasSize, height: canvasSize }}>
          <a
            href={binaryViewUrl || "#"}
            target="_blank"
            onClick={async (e) => {
              e.stopPropagation();
              if (!dbBinaryBodyAvailable) {
                e.preventDefault();
                if (storageType === "db" && loadDbBinaryBody) {
                  const loaded = await loadDbBinaryBody();
                  if (loaded) {
                    setLoadedDbBinaryValue(loaded);
                    try {
                      const loadedMeta = JSON.parse(loaded) as DbBinaryJson;
                      const loadedMime = loadedMeta.mime ?? null;
                      if (
                        loadedMime?.startsWith("image/") ||
                        loadedMime?.startsWith("video/") ||
                        loadedMime === "application/pdf" ||
                        loadedMime?.startsWith("text/") ||
                        loadedMime === "application/json" ||
                        loadedMime === "application/xml"
                      ) {
                        setTimeout(() => openPreview(loadedMime), 0);
                      } else if (loadedMeta.body_base64) {
                        window.open(
                          `data:${loadedMime ?? "application/octet-stream"};base64,${loadedMeta.body_base64}`,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    } catch (err) {
                      console.error("dbbinary loaded parse error:", err);
                    }
                  }
                }
                return;
              }
              if (isImage || isVideo || isPdf || isEml || isText) {
                e.preventDefault();
                openPreview(effectiveMeta?.mime);
              }
            }}
          >
            <img
              src={thumbUrl}
              className="binary-thumb-img"
              style={{ width: canvasSize, height: canvasSize }}
            />
          </a>
        </div>
      </div>




      {isImage && imageOpen && (
        <MyModal onClose={() => setImageOpen(false)}>
          <div className="modal-header">
            <i className="far fa-image"></i>
            <span>{effectiveMeta?.original_name}</span>
          </div>

          <div
            className="modal-body"
            style={{
              width: "80vw",
              height: "80vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "#111",
            }}
          >
            <img
              src={binaryViewUrl}
              alt={effectiveMeta?.original_name ?? ""}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </div>

          <div className="modal-footer">
            <a
              href={binaryDownloadUrl}
              download={effectiveMeta?.original_name ?? undefined}
            >
              ダウンロード
            </a>
          </div>
        </MyModal>
      )}

      {isVideo && videoOpen && (
        <MyModal onClose={() => setVideoOpen(false)}>
          <div className="modal-header">
            <i className="fas fa-photo-video"></i>
            <span>{effectiveMeta?.original_name}</span>
          </div>
          <div className="modal-body">
            <VideoPlayer
              src={binaryViewUrl}
              title={effectiveMeta?.original_name ?? ""}
            />
          </div>
          <div className="modal-footer">
            <a
              href={binaryDownloadUrl}
              download={effectiveMeta?.original_name ?? undefined}
            >
              ダウンロード
            </a>
          </div>
        </MyModal>
      )}

      {isPdf && pdfOpen && (
        <MyModal onClose={() => setPdfOpen(false)}>
          <div className="modal-header">
            <i className="far fa-file-pdf" />
            <span>{effectiveMeta?.original_name}</span>
          </div>
          <div style={{ width: "80vw", height: "80vh" }}>
            <iframe
              className="modal-body"
              src={binaryViewUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          </div>
          <div className="modal-footer">
            <a
              href={binaryDownloadUrl}
              download={effectiveMeta?.original_name ?? undefined}
            >
              ダウンロード
            </a>
          </div>
        </MyModal>
      )}

      {isEml && emlOpen && (
        <MyModal onClose={() => setEmlOpen(false)}>
          <div className="modal-header">
            <i className="fas fa-envelope"></i>
            <span>{effectiveMeta?.original_name}</span>
          </div>
          <div className="modal-body" style={{ width: "80vw", height: "80vh" }}>
            <iframe
              src={emlToHtmlUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          </div>
          <div className="modal-footer">
            <a
              href={binaryDownloadUrl}
              download={effectiveMeta?.original_name ?? undefined}
            >
              ダウンロード
            </a>
          </div>
        </MyModal>
      )}

      {isText && textOpen && (
        <MyModal onClose={() => setTextOpen(false)}>
          <div className="modal-header">
            <i className="far fa-file-alt"></i>
            <span>{effectiveMeta?.original_name}</span>
          </div>
          {storageType === "db" ? (
            <pre
              className="modal-body binary-text-viewer"
              style={{
                width: "80vw",
                height: "80vh",
                overflow: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              {dbBinaryText}
            </pre>
          ) : (
            <iframe
              className="modal-body"
              src={binaryViewUrl}
              style={{
                width: "80vw",
                height: "80vh",
                border: "none",
              }}
            />
          )}
          <div className="modal-footer">
            <a
              href={binaryDownloadUrl}
              download={effectiveMeta?.original_name ?? undefined}
            >
              ダウンロード
            </a>
          </div>
        </MyModal>
      )}
    </>
  );
};

export default BinaryViewer;
